import React, { useState, useEffect } from 'react';
import { Form, DatePicker, Input, Button, Upload, Modal, message, Card, Descriptions, Tag } from 'antd';
import { postRequest, getRequest } from '../../../../service';
import { getEmployeeId, getEmployeeData } from '../../../../utils/localStorage';
import { formatDate } from '../../../../utils/dateTime';

const Resignation = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [formData, setFormData] = useState(null);
  const [existingResignation, setExistingResignation] = useState(null);
  const [checkingResignation, setCheckingResignation] = useState(true);
  const [employeeList, setEmployeeList] = useState();
  const employeeDetails = getEmployeeData();

  const handleSubmit = (values) => {
    // Store form data and show confirmation modal
    setFormData(values);
    setConfirmModalVisible(true);
  };

  const handleConfirmSubmit = async () => {
    if (!formData) return;

    setLoading(true);
    try {
      const employeeData = getEmployeeId();
      const payload = {
        employeeId: [employeeData],
        resignationDate: formData.resignDate.format('YYYY-MM-DD'),
        status: "Resigned",
        lastWorkingDate: formData.lastWorkingDate.format('YYYY-MM-DD'),
        noticePeriod: parseInt(formData.noOfDays) || 0,
        reason: formData.reason,
        statusResign: "pending"
      };

      const response = await postRequest('/api/offboarding', payload);
      
      if (response.message === 'Offboarding record created successfully') {
        const managementIds = (employeeList?.managementChain || []).map(m => m?._id).filter(Boolean);
        const uniqueReceiverIds = Array.from(new Set([employeeDetails?.companyId, ...managementIds].filter(Boolean)));
        const res = await postRequest('/api/notifications', {
          "title": `${employeeList.employee.name} Resigned`,
          "senderId": employeeData,
          "receiverIds": uniqueReceiverIds,
          "type": "Offboarding",
          "relatedTypeId": response._id,
          "relatedDocumentModel": "Offboarding",
          "companyId": employeeDetails?.companyId,
          "priority": "high"
        });
        message.success('Resignation request submitted successfully');
        form.resetFields();
        setConfirmModalVisible(false);
        setFormData(null);
        checkExistingResignation();
      } else {
        message.error('Failed to submit resignation request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting resignation:', error);
      message.error('Failed to submit resignation request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setConfirmModalVisible(false);
    setFormData(null);
  };

  const handleFormCancel = () => {
    form.resetFields();
  };

  const checkExistingResignation = async () => {
    try {
      setCheckingResignation(true);
      const employeeData = getEmployeeId();
      const response = await getRequest(`/api/offboarding/byEmployee/${employeeData}`);
      
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        setExistingResignation(response.data[0]);
      }
    } catch (error) {
      console.error('Error checking existing resignation:', error);
    } finally {
      setCheckingResignation(false);
    }
  };
  const fetchEmployeeList = async () => {
    const employeeData = getEmployeeId();
    try {
      const response = await getRequest(`/api/companies/teammembers?employeeId=${employeeData}`);
      if (response?.employee) {
        setEmployeeList(response);
      }
    } catch (error) {
      console.error('Error fetching employee data:', error);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    fetchEmployeeList();
    checkExistingResignation();
  }, []);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      case 'pending':
        return 'orange';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDate(dateString);
  };

  return (
    <div className="p-6">
      <div className="w-full">
        <h4 className="text-2xl font-semibold mb-2">Resignation Request</h4>
        
        {checkingResignation ? (
          <div className="flex justify-center items-center h-32">
            <div className="text-gray-500">Checking resignation status...</div>
          </div>
        ) : existingResignation ? (
          <Card className="w-full">
            <div className="mb-4">
              <h5 className="text-lg font-medium mb-2">Your Resignation Details</h5>
              <Tag color={getStatusColor(existingResignation.statusResign)} className="mb-3">
                Status: {existingResignation.statusResign?.toUpperCase()}
              </Tag>
            </div>
            
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Employee Name">
                {existingResignation.employeeId?.[0]?.firstName} {existingResignation.employeeId?.[0]?.lastName}
              </Descriptions.Item>
              <Descriptions.Item label="Employee Code">
                {existingResignation.employeeId?.[0]?.employee_code}
              </Descriptions.Item>
              <Descriptions.Item label="Resignation Date">
                {formatDate(existingResignation.resignationDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Working Date">
                {formatDate(existingResignation.lastWorkingDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Notice Period">
                {existingResignation.noticePeriod} days
              </Descriptions.Item>
              <Descriptions.Item label="Reason for Resignation">
                <div className="whitespace-pre-wrap">{existingResignation.reason}</div>
              </Descriptions.Item>
              <Descriptions.Item label="Submitted On">
                {formatDate(existingResignation.createdAt)}
              </Descriptions.Item>
            </Descriptions>
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-blue-800">
                Your resignation request has been submitted and is currently under review. 
                You will be notified once the status changes.
              </p>
            </div>
          </Card>
        ) : (
          // Show resignation form
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
          <div className="flex flex-wrap gap-4">
            <Form.Item className='flex-1 min-w-[200px]' name="resignDate" label="Resignation Date" rules={[{ required: true, message: 'Please select resignation date' }]}>
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item className='flex-1 min-w-[200px]' name="lastWorkingDate" label="Last Working Date" rules={[{ required: true, message: 'Please select last working date' }]}>
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item className='flex-1 min-w-[200px]' name="noOfDays" label="Notice Period (Days)" rules={[{ required: true, message: 'Please enter number of days' }]}>
              <Input type="number" className="w-full" placeholder="Enter number of days" min={0} />
            </Form.Item>
          </div>

          <Form.Item
            name="reason"
            label="Reason for Resignation"
            rules={[{ required: true, message: 'Please provide reason for resignation' }]}
          >
            <Input.TextArea 
              rows={10}
              placeholder="Please provide detailed reason for your resignation"
            />
          </Form.Item>

          <div className="flex justify-end gap-4 mt-6">
            <Button type="default" onClick={handleFormCancel}>
              Cancel
            </Button>
            <Button 
              type="primary"
              htmlType="submit"
              className="bg-primary text-white hover:bg-primary"
            >
              Submit Resignation
            </Button>
          </div>
        </Form>
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal
        title="Confirm Resignation Submission"
        open={confirmModalVisible}
        onOk={handleConfirmSubmit}
        onCancel={handleCancel}
        confirmLoading={loading}
        okText="Confirm Submit"
        cancelText="Cancel"
        okButtonProps={{
          className: "bg-primary text-white hover:bg-primary"
        }}
      >
        <div className="py-4">
          <p className="text-gray-700 mb-4">
            Are you sure you want to submit your resignation request? This action cannot be undone.
          </p>
          
          {formData && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="font-medium mb-2">Resignation Details:</h5>
              <div className="space-y-2 text-sm">
                <div><strong>Resignation Date:</strong> {formData.resignDate?.format('DD/MM/YYYY')}</div>
                <div><strong>Last Working Date:</strong> {formData.lastWorkingDate?.format('DD/MM/YYYY')}</div>
                <div><strong>Notice Period:</strong> {formData.noOfDays} days</div>
                <div><strong>Reason:</strong> {formData.reason?.substring(0, 100)}{formData.reason?.length > 100 ? '...' : ''}</div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Resignation;
